import { ContentType } from "./http-client";

function cleanParams(params = {}) {
  const cleaned = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") {
      cleaned[key] = value;
    }
  }
  return cleaned;
}

export class MaintenanceParts {
  constructor(http) {
    this.http = http;
  }

  /**
   * Search and list master catalog SKU items.
   * @param {Object} [params] - { search, q, category, activeOnly, isActive, limit, offset }
   * @param {Object} [requestOptions]
   */
  listPartsCatalog = (
    { search, q, category, activeOnly, isActive, limit = 50, offset = 0 } = {},
    requestOptions = {}
  ) => {
    const query = cleanParams({
      q: search !== undefined ? search : q,
      category,
      isActive: activeOnly !== undefined ? activeOnly : isActive,
      limit,
      offset,
    });

    return this.http.request({
      path: "/api/maintenance/parts-catalog",
      method: "GET",
      query,
      secure: true,
      format: "json",
      ...requestOptions,
    });
  };

  /**
   * Create a new catalog SKU item.
   * @param {Object} data - { sku, name, description, category, unitOfMeasure, unitCost, unitPrice, isActive }
   * @param {Object} [requestOptions]
   */
  createPartsCatalogItem = (data, requestOptions = {}) =>
    this.http.request({
      path: "/api/maintenance/parts-catalog",
      method: "POST",
      body: data,
      secure: true,
      type: ContentType.Json,
      format: "json",
      ...requestOptions,
    });

  /**
   * Retrieve details for a specific catalog part item.
   * @param {string} partItemId
   * @param {Object} [requestOptions]
   */
  getPartsCatalogItem = (partItemId, requestOptions = {}) =>
    this.http.request({
      path: `/api/maintenance/parts-catalog/${partItemId}`,
      method: "GET",
      secure: true,
      format: "json",
      ...requestOptions,
    });

  /**
   * Update fields on an existing catalog part item.
   * @param {string} partItemId
   * @param {Object} data
   * @param {Object} [requestOptions]
   */
  updatePartsCatalogItem = (partItemId, data, requestOptions = {}) =>
    this.http.request({
      path: `/api/maintenance/parts-catalog/${partItemId}`,
      method: "PATCH",
      body: data,
      secure: true,
      type: ContentType.Json,
      format: "json",
      ...requestOptions,
    });

  /**
   * Delete an unconsumed catalog part item.
   * @param {string} partItemId
   * @param {Object} [requestOptions]
   */
  deletePartsCatalogItem = (partItemId, requestOptions = {}) =>
    this.http.request({
      path: `/api/maintenance/parts-catalog/${partItemId}`,
      method: "DELETE",
      secure: true,
      format: "json",
      ...requestOptions,
    });

  /**
   * List stock records across warehouse/property locations for a part.
   * @param {string} partItemId
   * @param {Object} [requestOptions]
   */
  getPartItemStocks = (partItemId, requestOptions = {}) =>
    this.http.request({
      path: `/api/maintenance/parts-catalog/${partItemId}/stocks`,
      method: "GET",
      secure: true,
      format: "json",
      ...requestOptions,
    });

  /**
   * Add or increment inventory stock for a part at a location.
   * @param {string} partItemId
   * @param {Object} data
   * @param {Object} [requestOptions]
   */
  addPartItemStock = (partItemId, data, requestOptions = {}) =>
    this.http.request({
      path: `/api/maintenance/parts-catalog/${partItemId}/stocks`,
      method: "POST",
      body: data,
      secure: true,
      type: ContentType.Json,
      format: "json",
      ...requestOptions,
    });

  /**
   * List all parts logged/consumed on a maintenance request.
   * @param {string} requestId
   * @param {Object} [requestOptions]
   */
  getTicketParts = (requestId, requestOptions = {}) =>
    this.http.request({
      path: `/api/maintenance/requests/${requestId}/parts`,
      method: "GET",
      secure: true,
      format: "json",
      ...requestOptions,
    });

  /**
   * Allocate or log a part onto a maintenance request.
   * Decrements stock if stockId is provided and recalculates request.total_cost.
   * @param {string} requestId
   * @param {Object} data - { partItemId, stockId, customPartName, sku, quantity, unitCost, warehouseLocation, payerSplit, notes }
   * @param {Object} [requestOptions]
   */
  allocateTicketPart = (requestId, data, requestOptions = {}) =>
    this.http.request({
      path: `/api/maintenance/requests/${requestId}/parts`,
      method: "POST",
      body: data,
      secure: true,
      type: ContentType.Json,
      format: "json",
      ...requestOptions,
    });

  /**
   * Remove a part from a maintenance request.
   * Restores inventory stock if linked and recalculates request.total_cost.
   * @param {string} requestId
   * @param {string} ticketPartId
   * @param {Object} [requestOptions]
   */
  deleteTicketPart = (requestId, ticketPartId, requestOptions = {}) =>
    this.http.request({
      path: `/api/maintenance/requests/${requestId}/parts/${ticketPartId}`,
      method: "DELETE",
      secure: true,
      format: "json",
      ...requestOptions,
    });

  // Backward-compatible & ergonomic method aliases
  listCatalog = (params, options) => this.listPartsCatalog(params, options);
  getCatalogPart = (id, options) => this.getPartsCatalogItem(id, options);
  createCatalogPart = (data, options) => this.createPartsCatalogItem(data, options);
  listStocksForPart = (id, options) => this.getPartItemStocks(id, options);
  listTicketParts = (requestId, options) => this.getTicketParts(requestId, options);
  addTicketPart = (requestId, data, options) => this.allocateTicketPart(requestId, data, options);
}

export default MaintenanceParts;
